<!-- Show startup loader during initialization -->
@if (initializing()) {
<app-startup-loader [message]="initializationMessage()" />
} @else if (showSyncLoader()) {
<app-startup-loader [message]="'startup.downloadingData' | translate" data-testid="sync-startup-loader" />
}

<!-- Native shell: brief overlay while restarting into a downloaded update -->
@if (liveUpdateService.restarting()) {
<app-update-overlay />
}

<!-- Show paused overlay when this tab is not the DB leader -->
@if (syncService.isPaused()) {
<app-paused-overlay />
}

<app-toolbar data-testid="app-toolbar">
  @if (authService.isAuthenticated()) {
  <app-button variant="icon" (click)="sidenav().toggle()" class="nav-icon"
    [attr.aria-label]="'nav.openMenu' | translate" data-testid="nav-icon">
    <app-icon name="menu" />
  </app-button>
  }
  <span class="logo-wrapper clickable" (click)="navigateToHome()" (keydown.enter)="navigateToHome()"
    (keydown.space)="navigateToHome()" tabindex="0" role="button" [attr.aria-label]="'nav.goToHome' | translate"
    data-testid="app-title">
    <img [src]="'logos/checkpoint-logo-horizontal.png' | envPrefix:'brand'" alt="CheckPoint"
      class="logo logo-sm logo-horizontal" />
    <img [src]="'logos/checkpoint-icon.png' | envPrefix:'brand'" alt="CheckPoint" class="logo logo-sm logo-icon" />
  </span>
  @if (authService.isAuthenticated()) {
  <app-breadcrumb></app-breadcrumb>
  }
  <span class="spacer"></span>
  @if (!authService.isAuthenticated()) {
  <app-button variant="icon" (click)="toggleStreamLogs()" data-testid="send-logs-button"
    [tippy]="streamLogsEnabled() ? 'Stop streaming logs to backend' : 'Start streaming logs to backend'"
    [class.active-streaming]="streamLogsEnabled()">
    <app-icon [name]="streamLogsEnabled() ? 'radio' : 'upload'" />
  </app-button>
  }
  @if (authService.isAuthenticated()) {
  <div class="user-menu-dropdown" [class.open]="userDropdownOpen()" (clickOutside)="closeUserDropdown()">
    <div class="user-menu" (click)="toggleUserDropdown()" (keydown.enter)="toggleUserDropdown()"
      (keydown.space)="toggleUserDropdown()" tabindex="0" role="button">
      @if (getUserAvatar(); as avatarSrc) {
      <img [src]="avatarSrc" [alt]="displayUser()?.full_name || ''" class="user-avatar"
        data-testid="user-avatar" />
      }
      <div class="user-info">
        <span class="user-name" data-testid="user-name">{{ displayUser()?.full_name }}</span>
        @if (organizationService.currentOrganization()) {
        <span class="org-name" data-testid="org-name">{{ organizationService.currentOrganization()?.name }}</span>
        }
      </div>
    </div>
    <div class="user-dropdown-menu">
      <div class="dropdown-user-info">
        <span class="dropdown-user-name" data-testid="dropdown-user-name">{{ displayUser()?.full_name
          }}</span>
        @if (organizationService.currentOrganization()) {
        <span class="dropdown-org-name" data-testid="dropdown-org-name">{{
          organizationService.currentOrganization()?.name
          }}</span>
        }
      </div>
      <button class="dropdown-item" (click)="toggleStreamLogsAndCloseDropdown()" data-testid="send-logs-menu-item"
        [class.active-streaming]="streamLogsEnabled()">
        <app-icon [name]="streamLogsEnabled() ? 'radio' : 'upload'" />
        <span>{{ streamLogsEnabled() ? 'Stop Streaming Logs' : 'Stream Logs to Backend' }}</span>
      </button>
      <button class="dropdown-item" (click)="logout()" data-testid="logout-button">
        <app-icon name="log-out" />
        <span>{{ 'nav.logout' | translate }}</span>
      </button>
    </div>
  </div>
  }
</app-toolbar>

@if (authService.isAuthenticated()) {
<app-sidenav>
  <nav sidenav class="nav-list nav-section">
    <a app-nav-list-item routerLink="/">
      <img [src]="'logos/checkpoint-icon.png' | envPrefix" [attr.alt]="'nav.home' | translate" class="home-icon" />
      <span>{{ 'nav.home' | translate }}@if (envLabel) { <small class="env-label" data-testid="nav-env-label">({{ 'nav.envLabel' | translate:{ name: envLabel } }})</small>}</span>
    </a>
    <a app-nav-list-item routerLink="/scan">
      <app-icon name="scan" />
      <span>{{ 'nav.scanQrCode' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/checkpoints" *hasPermission="'asset.read'">
      <app-icon name="package" />
      <span>{{ 'nav.assets' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/forms" *hasPermission="'form.read'">
      <app-icon name="folder" />
      <span>{{ 'nav.interactionTypes' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/lookup-lists" *hasPermission="'lookup-list.read'">
      <app-icon name="list" />
      <span>{{ 'nav.lookupLists' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/onboarding" *hasPermission="'asset.create'"
      data-testid="nav-getting-started">
      <app-icon name="compass" />
      <span>{{ 'nav.gettingStarted' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/reports" *hasPermission="'reports.view'">
      <app-icon name="chart-column" />
      <span>{{ 'nav.reports' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/team">
      <app-icon name="users" />
      <span>{{ 'nav.team' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/organizations">
      <app-icon name="building" />
      <span>{{ 'nav.switchOrganization' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/settings" *hasPermission="'org.settings'">
      <app-icon name="settings" />
      <span>{{ 'nav.settings' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/diagnostics" *hasPermission="'diagnostics.view'">
      <app-icon name="activity" />
      <span>{{ 'nav.diagnostics' | translate }}</span>
    </a>
    <a app-nav-list-item routerLink="/dev/sql" *hasPermission="'sql.view'">
      <app-icon name="code" />
      <span>{{ 'nav.devSqlConsole' | translate }}</span>
    </a>
  </nav>

  <div class="sidenav-footer" sidenav>
    <div class="sidenav-user-info clickable">
      @if (getUserAvatar(); as avatarSrc) {
      <img [src]="avatarSrc" [alt]="displayUser()?.full_name || ''" class="sidenav-user-avatar" />
      }
      <div class="sidenav-user-details">
        <span class="sidenav-user-name">{{ displayUser()?.full_name }}</span>
        @if (organizationService.currentOrganization()) {
        <span class="sidenav-org-name">{{ organizationService.currentOrganization()?.name }}</span>
        }
      </div>
    </div>
    <app-button variant="text" (click)="logout()" data-testid="sidenav-logout-button"
      class="sidenav-logout-button clickable">
      <app-icon name="log-out" />
      <span>{{ 'nav.logout' | translate }}</span>
    </app-button>

  </div>

  <!-- Info section - passive information -->
  <div class="flex flex-column flex-grow flex-justify-end" sidenav>
    <div class="info-section flex flex-align-center flex-justify-space-between gap-sm">
      <div class="info-item" data-testid="online-badge-sidenav">
        <app-network-status-badge />
      </div>
      <div class="info-item build-info" data-testid="build-info-sidenav">
        {{ buildTimestamp | dateTimezone }}
      </div>
    </div>
  </div>


  <router-outlet></router-outlet>
</app-sidenav>
} @else {
<div class="main-content">
  <router-outlet></router-outlet>
</div>
}

@if (authService.isAuthenticated()) {
<app-tour-overlay />
}

<app-toast />

<!-- Database reset dialog (shown when backend sync generation changes) -->
@if (showDatabaseResetDialog()) {
<app-database-reset-dialog (reset)="handleDatabaseReset($event)" />
}

<!-- Global entity list selector dialog -->
<app-entity-list-selector-dialog />

<!-- PWA install modal (shown for uninstalled users on a 3-day snooze cycle) -->
@if (pwaInstallService.showModal()) {
<app-pwa-install-modal />
}

<!-- Dynamic modal system -->
<app-modal-container />